import React, { useRef, useState, useEffect, useCallback } from 'react';
import VideoMesh from '../VideoMesh/VideoMesh';
import { useThree } from '@react-three/fiber';
import { Vector3, MeshBasicMaterial, Euler } from 'three';
import TextMesh from '../TextMesh/TextMesh';
import { UserAudio } from '../../../models/UserAudio';

import { throttle } from 'lodash';
import { calculateAngleInfoFromSourceToTarget } from '../../../utils/math';
import useCameraState from '../Camera/UseCameraState';
import { userAvatarMaterial, userAvatarBodyGeometry } from '../SharedMeshes/SharedMeshes';

const UserAvatar: React.FC<{ userName: string, position: Vector3, rotation: Euler, userAudio: UserAudio | undefined, videoTrackOrSrc: MediaStreamTrack | string | undefined }> = ({ userName, position, rotation, userAudio, videoTrackOrSrc }) => {
  const [textRotationY, setTextRotationY] = useState<number>(0);

  const { camera } = useThree();
  const { cameraPosition, cameraRotation } = useCameraState(camera);

  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | undefined>(undefined);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);

  const videoHeight = 1.8;
  const bodyHeight = 1.2;
  const paddingHeight = 0.5;

  useEffect(() => {
    if (videoTrackOrSrc instanceof MediaStreamTrack) setVideoTrack(videoTrackOrSrc);
    else setVideoSrc(videoTrackOrSrc);
  }, [videoTrackOrSrc]);
  
  const throttledUpdateAudioFilters = useCallback(
    throttle(() => {
      if (!userAudio) return;
      const camPosition = camera.position.clone();
      const camRotation = camera.rotation.clone();
      userAudio.updateAudioFilters(camPosition, camRotation, position, rotation);
    }, 16),
    [userAudio, cameraPosition, cameraRotation, position, rotation]
  );

  const throttledSetTextRotationY = useCallback(
    throttle(() => {
      const angleToMesh = calculateAngleInfoFromSourceToTarget(position, rotation, camera.position.clone());
      setTextRotationY(angleToMesh.clockwiseAngleRadians + Math.PI);
    }, 16),
    [position, rotation, cameraPosition]
  );

  useEffect(() => {
    throttledUpdateAudioFilters();
  }, [userAudio, position, rotation, cameraPosition, cameraRotation]);

  useEffect(() => {
    throttledSetTextRotationY();
  }, [cameraPosition, position, rotation])

  return (
    <>
      <mesh position={position} rotation={[rotation.x, rotation.y, rotation.z]}>
        <TextMesh text={userName ?? 'anon'} rotationY={textRotationY} positionY={bodyHeight + videoHeight + paddingHeight} />
        <VideoMesh track={videoTrack} videoHeight={videoHeight} trackSrc={videoSrc} />
        <mesh position={[0, 0, 0]}>
          {userAvatarMaterial}
          {userAvatarBodyGeometry}
        </mesh>
      </mesh>
    </>
  );
};

export default UserAvatar;
